import type { CSSProperties, ReactNode, RefObject } from 'react';
import type { ImageField, LinkField, RichTextField } from '@prismicio/client';
import { asText, isFilled } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { PrismicRichText } from '@prismicio/react';
import { PrimaryGreenCtaButton } from '@/components/common/PrimaryGreenCtaButton';
import { cn } from '@/lib/utils';

type Theme = 'light' | 'dark' | null;

export type HorizontalContentCardsProps = {
  sliceType: string;
  variation: string;
  theme?: Theme;
  backgroundImage?: ImageField | null;
  headline?: RichTextField | null;
  subheadline?: RichTextField | null;
  summary?: RichTextField | null;
  /**
   * Classes merged onto the cards `grid` container after the base `grid`
   * class. Omit to keep the original three-up steps row
   * (`grid-cols-1 gap-6 lg:grid-cols-3`).
   */
  gridClassName?: string;
  /**
   * Inline styles for the cards container, for values Tailwind cannot express
   * — e.g. a column count derived from how many cards there are.
   */
  gridStyle?: CSSProperties;
  /**
   * Classes merged onto the headline/summary block, which is capped at
   * `max-w-[900px]` (summary at `max-w-[720px]`) by default. Pass `max-w-none`
   * for layouts whose header runs the full width of the section.
   */
  headerClassName?: string;
  summaryClassName?: string;
  /**
   * Classes appended to the summary paragraph's own defaults (size, weight,
   * leading and the theme text colour). Merged with `cn`, so anything passed
   * here overrides the default it conflicts with — e.g. `text-[#041118]`
   * replaces the muted `text-[#041118]/75` while the font sizes survive.
   */
  summaryParagraphClassName?: string;
  /**
   * Optional weight for bold spans in the summary. Preflight resolves
   * `strong { font-weight: bolder }` to 400 inside the light-weight paragraph,
   * so layouts that rely on bold emphasis opt in here. Omit to keep the
   * original rendering.
   */
  summaryStrongClassName?: string;
  /**
   * Optional rich-text block rendered *below* the cards grid, as a sibling
   * inside the shell's `flex flex-col gap-9` — so the 36px gap between the
   * cards row and the footnote falls out of the parent's gap. Uses the same
   * paragraph styling as the summary by default. Only rendered when the field
   * is filled.
   */
  footnote?: RichTextField | null;
  /** Wrapper classes for the footnote block; full-width by default. */
  footnoteClassName?: string;
  /**
   * Classes appended to the footnote paragraph's own defaults (size, weight,
   * leading and the theme text colour). Same merge behaviour as
   * `summaryParagraphClassName`.
   */
  footnoteParagraphClassName?: string;
  /**
   * Optional ref to the cards `grid` container. Client-side layouts that need
   * to observe or drive the row directly (e.g. a horizontally scrollable
   * rail that translates wheel gestures into `scrollLeft`) attach behavior
   * through this ref without forking the shell.
   */
  gridRef?: RefObject<HTMLDivElement | null>;
  /**
   * Optional primary CTA rendered between the cards row and the footnote
   */
  primaryCta?: LinkField | null;
  children: ReactNode;
};

export type HorizontalContentCardProps = {
  className?: string;
  /** Escape hatch for values Tailwind cannot express, e.g. a background image. */
  style?: CSSProperties;
  children: ReactNode;
};

const DEFAULT_GRID_CLASS = 'grid-cols-1 gap-6 lg:grid-cols-3';

const CARD_SHELL_CLASS =
  'flex flex-col gap-[25px] rounded-[10px] bg-[#f3f3f3] p-5';

export function HorizontalContentCards({
  sliceType,
  variation,
  theme,
  backgroundImage,
  headline,
  subheadline,
  summary,
  gridClassName,
  gridStyle,
  headerClassName,
  summaryClassName,
  summaryParagraphClassName,
  summaryStrongClassName,
  footnote,
  footnoteClassName,
  footnoteParagraphClassName,
  gridRef,
  primaryCta,
  children,
}: HorizontalContentCardsProps) {
  const isDark = theme === 'dark';
  const hasHeadline = isFilled.richText(headline);
  const hasSubheadline = isFilled.richText(subheadline);

  return (
    <section
      data-slice-type={sliceType}
      data-slice-variation={variation}
      className={`relative overflow-hidden px-5 py-16 md:px-[30px] md:py-20 lg:py-[70px] ${
        isDark ? 'bg-[#262626]' : 'bg-white text-[#041118]'
      }`}
    >
      {isFilled.image(backgroundImage) && (
        <PrismicNextImage
          field={backgroundImage}
          fill
          sizes="100vw"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
          fallbackAlt=""
        />
      )}

      <div className="relative mx-auto flex w-full max-w-[1280px] flex-col gap-9">
        <div className={cn('max-w-[900px]', headerClassName)}>
          {(hasHeadline || hasSubheadline) && (
            <h2 className="text-balance break-words text-[28px] font-medium leading-[1.2] md:text-[42px]">
              {hasHeadline && (
                <span className={isDark ? 'text-[#05f273]' : 'text-[#aaa]'}>
                  {asText(headline)}
                </span>
              )}
              {hasHeadline && hasSubheadline && <br />}
              {hasSubheadline && (
                <span className={isDark ? 'text-white' : 'text-[#041118]'}>
                  {asText(subheadline)}
                </span>
              )}
            </h2>
          )}
          {isFilled.richText(summary) && (
            <div className={cn('mt-9 max-w-[720px]', summaryClassName)}>
              <PrismicRichText
                field={summary}
                components={{
                  paragraph: ({ children: paragraphChildren }) => (
                    <p
                      className={cn(
                        'text-[16px] font-light leading-[1.5] md:text-[18px]',
                        isDark ? 'text-white/75' : 'text-[#041118]/75',
                        summaryParagraphClassName,
                      )}
                    >
                      {paragraphChildren}
                    </p>
                  ),
                  ...(summaryStrongClassName && {
                    strong: ({ children: strongChildren }) => (
                      <strong className={summaryStrongClassName}>
                        {strongChildren}
                      </strong>
                    ),
                  }),
                }}
              />
            </div>
          )}
        </div>

        <div
          ref={gridRef}
          className={cn('grid', gridClassName ?? DEFAULT_GRID_CLASS)}
          style={gridStyle}
        >
          {children}
        </div>

        {isFilled.link(primaryCta) && (
          <div>
            <PrimaryGreenCtaButton field={primaryCta} />
          </div>
        )}

        {isFilled.richText(footnote) && (
          <div className={cn('max-w-none', footnoteClassName)}>
            <PrismicRichText
              field={footnote}
              components={{
                paragraph: ({ children: paragraphChildren }) => (
                  <p
                    className={cn(
                      'text-[16px] font-light leading-[1.5] md:text-[18px]',
                      isDark ? 'text-white/75' : 'text-[#041118]/75',
                      footnoteParagraphClassName,
                    )}
                  >
                    {paragraphChildren}
                  </p>
                ),
              }}
            />
          </div>
        )}
      </div>
    </section>
  );
}

export function HorizontalContentCard({
  className,
  style,
  children,
}: HorizontalContentCardProps) {
  return (
    <div className={cn(CARD_SHELL_CLASS, className)} style={style}>
      {children}
    </div>
  );
}
